import React from 'react';
import styles from './strength-bar.module.scss';
import cn from 'classnames';
import text from './strength-bar.text.json';

export type Level = {
  text: string;
  upToPercent: number;
  customBarClass?: string;
  customTextClass?: string;
};

const ORDER = ['start', 'red', 'orange', 'yellow', 'green'] as const;
type LevelKey = (typeof ORDER)[number];

export type StrengthBarConfig = {
  [key in LevelKey]: Level;
};

export const DEFAULT_CONFIG: StrengthBarConfig = {
  start: { text: text.DEFAULT_START, upToPercent: 0 },
  red: { text: text.DEFAULT_RED, upToPercent: 25 },
  orange: { text: text.DEFAULT_ORANGE, upToPercent: 40 },
  yellow: { text: text.DEFAULT_YELLOW, upToPercent: 75 },
  green: { text: text.DEFAULT_GREEN, upToPercent: 100 },
};

type Props = {
  currentInput: string;
  config: StrengthBarConfig;
  maxCharacters: number;
  disabled?: boolean;
};

const getPercentage = (currentInput: string, maxCharacters: number) =>
  // eslint-disable-next-line no-magic-numbers
  Math.floor(((currentInput?.length || 0) * 100) / maxCharacters);

const getLevel = (
  config: StrengthBarConfig,
  currentInput: string,
  maxCharacters: number,
) => {
  const percentage = getPercentage(currentInput, maxCharacters);
  const matchingLevel =
    ORDER.find((levelKey: LevelKey, index) => {
      const isBelowLevelCap = config[levelKey].upToPercent >= percentage;
      const isLastIndex = ORDER.length - 1 === index;

      return isBelowLevelCap || isLastIndex;
    }) || 'start';

  return {
    level: config[matchingLevel],
    levelName: matchingLevel,
  };
};

export const StrengthBar = ({ currentInput, config, maxCharacters }: Props) => {
  const percentage = getPercentage(currentInput, maxCharacters);
  const minNumForGreen = config.yellow.upToPercent;
  // eslint-disable-next-line no-magic-numbers
  const width = Math.min(percentage * (100 / minNumForGreen), 100);
  const { levelName } = getLevel(config, currentInput, maxCharacters);
  const customClass = config[levelName].customBarClass;

  return (
    <div className={styles.bar} data-testid="strength-bar">
      <span
        className={cn(styles.innerBar, customClass || styles[levelName])}
        style={{
          width: `${width}%`,
        }}
      ></span>
    </div>
  );
};

export const StrengthText = ({
  currentInput,
  config,
  maxCharacters,
  disabled = false,
}: Props) => {
  const { level, levelName } = getLevel(config, currentInput, maxCharacters);
  const customClass = config[levelName].customTextClass;

  return (
    <span
      className={cn(
        styles.text,
        customClass || styles[levelName],
        disabled && styles.disabled,
      )}
      data-testid="strength-text"
    >
      {level.text}
    </span>
  );
};
